import React from "react";
import styled from "styled-components";
import { Box, Flex, Image } from "rebass/styled-components";
import { FiInfo } from "react-icons/fi";
import { HiArrowRight, HiExclamation } from "react-icons/hi";

import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import cs2Logo from "MVVM/Assets/GameLogos/cs2.svg";

const ENVY = "#4dbd94";

const MotionBlurSvg = ({ ghostCount }: { ghostCount: number }) => {
  const size = 80;
  const cx = size / 2;
  const cy = size / 2;
  const squareSize = 30;
  const baseAngle = -12;

  const totalSpread = ghostCount <= 1 ? 0 : 22;
  const angleSpread = ghostCount <= 1 ? 0 : 10;

  const ghosts = [];
  for (let i = 0; i < ghostCount; i++) {
    const t = ghostCount <= 1 ? 0 : i / (ghostCount - 1);
    const offsetX = totalSpread * (t - 0.5);
    const angle = baseAngle + angleSpread * (t - 0.5);
    const opacity = ghostCount <= 1 ? 1 : 0.15 + 0.85 * t;

    ghosts.push(
      <rect
        key={i}
        x={cx + offsetX - squareSize / 2}
        y={cy - squareSize / 2}
        width={squareSize}
        height={squareSize}
        rx={3}
        fill={ENVY}
        opacity={opacity}
        transform={`rotate(${angle} ${cx + offsetX} ${cy})`}
      />,
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {ghosts}
    </svg>
  );
};

const BLUR_STEPS = [
  { label: "Disabled", ghostCount: 1 },
  { label: "Low", ghostCount: 3 },
  { label: "Medium", ghostCount: 5 },
  { label: "High", ghostCount: 10 },
];

const MotionBlurTooltipContent = () => (
  <TooltipBody>
    <TooltipTextSection>
      <Cs2Line>
        <Cs2Icon src={cs2Logo} alt="CS2" />
        This setting affects only CS2
      </Cs2Line>
      <TooltipParagraph>
        Creates a cinematic motion blur effect.
      </TooltipParagraph>
    </TooltipTextSection>
    <BlurStepsRow>
      {BLUR_STEPS.map((step, i) => (
        <React.Fragment key={step.label}>
          {i > 0 && (
            <ArrowSeparator>
              <HiArrowRight size={16} />
            </ArrowSeparator>
          )}
          <BlurStepItem>
            <MotionBlurSvg ghostCount={step.ghostCount} />
            <BlurStepLabel>{step.label}</BlurStepLabel>
          </BlurStepItem>
        </React.Fragment>
      ))}
    </BlurStepsRow>
    <WarningLine>
      <WarningIcon>
        <HiExclamation size={12} />
      </WarningIcon>
      Using higher settings can cause your clips to take up to a few minutes
      longer to process.
    </WarningLine>
  </TooltipBody>
);

export const MotionBlurTooltip = () => (
  <AnimatedTooltip
    content={<MotionBlurTooltipContent />}
    position="top"
    maxWidth={520}
  >
    <TooltipTrigger>
      <FiInfo size={14} />
    </TooltipTrigger>
  </AnimatedTooltip>
);

const TooltipBody = styled(Flex)`
  flex-direction: column;
  font-size: 14px;
  gap: 16px;
`;

const TooltipTextSection = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`;

const Cs2Line = styled(Flex)`
  align-items: center;
  font-weight: 600;
  gap: 6px;
`;

const Cs2Icon = styled(Image)`
  height: 18px;
  width: 18px;
`;

const TooltipParagraph = styled(Box)`
  line-height: 1.5;
`;

const BlurStepsRow = styled(Flex)`
  align-items: center;
  gap: 6px;
  justify-content: center;
  padding-top: 4px;
`;

const ArrowSeparator = styled(Flex)`
  align-items: center;
  color: rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
`;

const BlurStepItem = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 6px;
  min-width: 70px;
`;

const BlurStepLabel = styled(Box)`
  font-size: 13px;
  font-weight: 600;
  text-align: center;
`;

const TooltipTrigger = styled(Flex)`
  align-items: center;
  color: inherit;
  cursor: help;
  display: inline-flex;
  margin-left: 4px;
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const WarningLine = styled(Flex)`
  align-items: flex-start;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  gap: 6px;
  line-height: 1.5;
  padding-top: 12px;
`;

const WarningIcon = styled(Flex)`
  align-items: center;
  background: #f5a623;
  border-radius: 3px;
  color: #000;
  flex-shrink: 0;
  justify-content: center;
  margin-top: 2px;
  padding: 2px;
`;
